import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import './DICOMCreate.css';

const DICOMCreate = () => {
  const location = useLocation();
  const token = localStorage.getItem('token');

  const [patientPrefilled, setPatientPrefilled] = useState(false);
  const [studyPrefilled, setStudyPrefilled] = useState(false);

  const [formData, setFormData] = useState({
    patient: '',             // Required
    file_path: '',           // Required
    study: null,            // Optional
    series_id: null,           // Optional
    side: 'LCA',            // Required
    primary_angle: null,       // Optional
    secondary_angle: null,     // Optional
    dev_notes: null,           // Optional
    original_upload_path: '',// Required
    labels: null,             // Optional
    all_images_dir: null,      // Required
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get('patient');
    const studyId = searchParams.get('study');
    if (patientId) {
      setFormData((prev) => ({ ...prev, patient: patientId }));
      setPatientPrefilled(true);
    }
    if (studyId) {
      setFormData((prev) => ({ ...prev, study: studyId }));
      setStudyPrefilled(true);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };

    if (!payload.labels || !payload.labels.trim()) {
      payload.labels = null;
    } else {
      try {
        payload.labels = JSON.parse(payload.labels);
      } catch (err) {
        setError('Labels must be valid JSON (e.g. {"key":"value"}).');
        return;
      }
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/patients/dicoms/create/`,
        payload,
        { headers: { Authorization: `Token ${token}` } }
      );
      setSuccess(true);
      setError('');
      setFormData({
        patient: '',
        file_path: '',
        study: '',
        series_id: '',
        side: 'LCA',
        primary_angle: '',
        secondary_angle: '',
        dev_notes: '',
        original_upload_path: '',
        labels: '',
        all_images_dir: '',
      });
    } catch (err) {
      console.error(err);
      setError('Error creating DICOM. Check fields or try again.');
      setSuccess(false);
    }
  };

  return (
    <div className="dicom-create-container">
      <h3>Create New XA DICOM</h3>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">XA DICOM created successfully!</p>}

      <form onSubmit={handleSubmit} className="dicom-create-form">
        {/* Patient (Required) */}
        <div className="form-group">
          <label>
            Patient ID / UUID <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="patient"
            value={formData.patient}
            onChange={handleChange}
            placeholder="Patient's ID or UUID"
            required
            disabled={patientPrefilled}
          />
        </div>

        {/* File Path (Required) */}
        <div className="form-group">
          <label>
            File Path <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="file_path"
            value={formData.file_path}
            onChange={handleChange}
            required
          />
        </div>

        {/* Study ID (Optional) */}
        <div className="form-group">
          <label>Study ID (Optional)</label>
          <input
            type="text"
            name="study"
            value={formData.study}
            onChange={handleChange}
            disabled={studyPrefilled}
          />
        </div>

        {/* Series ID (Optional) */}
        <div className="form-group">
          <label>Series ID (Optional)</label>
          <input
            type="text"
            name="series_id"
            value={formData.series_id}
            onChange={handleChange}
          />
        </div>

        {/* Side (Required) */}
        <div className="form-group">
          <label>
            Side <span className="required-asterisk">*</span>
          </label>
          <select name="side" value={formData.side} onChange={handleChange} required>
            <option value="LCA">LCA</option>
            <option value="RCA">RCA</option>
            <option value="Both">Both</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {/* Primary Angle (Optional) */}
        <div className="form-group">
          <label>Primary Angle (Optional)</label>
          <input
            type="number"
            step="0.01"
            name="primary_angle"
            value={formData.primary_angle}
            onChange={handleChange}
          />
        </div>

        {/* Secondary Angle (Optional) */}
        <div className="form-group">
          <label>Secondary Angle (Optional)</label>
          <input
            type="number"
            step="0.01"
            name="secondary_angle"
            value={formData.secondary_angle}
            onChange={handleChange}
          />
        </div>

        {/* Original Upload Path (Required) */}
        <div className="form-group">
          <label>
            Original Upload Path <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            name="original_upload_path"
            value={formData.original_upload_path}
            onChange={handleChange}
            required
          />
        </div>

        {/* Dev Notes (Optional) */}
        <div className="form-group">
          <label>Dev Notes (Optional)</label>
          <textarea
            name="dev_notes"
            value={formData.dev_notes}
            onChange={handleChange}
          />
        </div>

        {/* Labels (Optional) */}
        <div className="form-group">
          <label>Labels (Optional)</label>
          <textarea
            name="labels"
            value={formData.labels}
            onChange={handleChange}
            placeholder='e.g. {"dominant":"Right","detail":"some note"}'
          />
        </div>

        {/* All Dicom Dir (Required) */}
        <div className="form-group">
          <label>All Dicom Dir (Required)<span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="all_images_dir"
            value={formData.all_images_dir}
            onChange={handleChange}
            required
          />
        </div>
        <button type="submit" className="dicom-create-btn">
          Create XA DICOM
        </button>
      </form>
    </div>
  );
};

export default DICOMCreate;
